import React from 'react';
import Sidebar from '../components/layout/Sidebar';
import { AuthenticatedHeader } from '../components/layout/AuthenticatedHeader';
import { useCrmData } from '../contexts/CrmProviders';
import { useOrganization } from '../contexts/OrganizationContext';
import Spinner from '../components/ui/Spinner';
import PageRenderer from '../router/PageRenderer';
import { useAppContext } from '../contexts/AppContext';
import { SubscriptionGuard } from '../features/subscription';
import SubscriptionAlerts from '../features/subscription/components/SubscriptionAlerts';
import GlobalConnectionAlerts from '../components/shared/GlobalConnectionAlerts';
import { useCheckoutBillingSync } from '../features/subscription/hooks/useCheckoutBillingSync';
import { useOnboardingGate } from '../features/onboarding/OnboardingGateContext';
import { usePermissions } from '../hooks/usePermissions';
import type { Page } from '../types';
import { HeaderProvider } from '../contexts/HeaderContext';
import { SuperadminLayout } from '../features/superadmin/layout/SuperadminLayout';
import { PartnerLayout } from '../features/partner/layout/PartnerLayout';
import { useSuperadminPageGuard } from '../features/superadmin/hooks/useSuperadminPageGuard';
import { usePartnerPageGuard } from '../features/partner/hooks/usePartnerPageGuard';
import { useMemberPageGuard } from '../features/settings/hooks/useMemberPageGuard';
import { isPartnerShellPage } from '../utils/routeUtils';
import { SupportModeBanner } from '../features/superadmin/impersonation/SupportModeBanner';
import { MOBILE_SHELL_TOP_SAFE_CLASS } from '../components/layout/layoutNavTokens';
import { cn } from '@/lib/utils';
import { PARTNER_PAGES } from '../utils/routeUtils';

const BILLING_ALLOWED_PAGES: Page[] = ['billing', 'subscription', 'profile', 'settings'];
const MEMBER_ALLOWED_PAGES: Page[] = ['profile', 'settings'];

/** Páginas liberadas no SubscriptionGuard conforme plano / checkout. */
function getGuardAllowedPages(
    effectiveNeedsPlan: boolean,
    isCheckoutProcessing: boolean,
    canViewBilling: boolean,
): Page[] {
    if (effectiveNeedsPlan) return ['subscription', ...PARTNER_PAGES];
    const base = canViewBilling ? BILLING_ALLOWED_PAGES : MEMBER_ALLOWED_PAGES;
    const pages: Page[] = isCheckoutProcessing ? ['dashboard', ...base] : [...base];
    return [...pages, ...PARTNER_PAGES];
}

type BootOverlayProps = {
    title: string;
    subtitle: string;
    fading?: boolean;
};

/** Overlay full-screen único para boot inicial e ativação de plano. */
const BootOverlay: React.FC<BootOverlayProps> = ({ title, subtitle, fading = false }) => (
    <div
        className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background transition-opacity duration-500 ease-in-out ${
            fading ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
    >
        <div className="relative mb-6">
            <div className="relative bg-card p-4 rounded-full shadow-lg flex items-center justify-center border border-border">
                <Spinner color="blue" size="lg" />
            </div>
        </div>
        <div className="text-center space-y-2">
            <h3 className="text-lg font-semibold text-default">{title}</h3>
            <p className="text-sm text-secondary font-medium animate-pulse">{subtitle}</p>
        </div>
    </div>
);

const AuthenticatedLayout: React.FC = () => {
    useSuperadminPageGuard();
    usePartnerPageGuard();
    useMemberPageGuard();
    const { isLoading: isCrmLoading } = useCrmData();
    const { isLoading: isOrgLoading, error: orgError } = useOrganization();
    const { isGateReady } = useOnboardingGate();
    const { currentPage } = useAppContext();
    const { canViewBilling } = usePermissions();
    const {
        isCheckoutProcessing,
        effectiveNeedsPlan,
        isSubscriptionBlocked,
    } = useCheckoutBillingSync();

    const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

    // Espera o gate de onboarding — evita piscada do dashboard no 1º acesso pós-cadastro.
    const isLoading = isCrmLoading || isOrgLoading || !isGateReady;
    const isOnboardingPage = currentPage === 'onboarding';
    const shouldHideNavigation =
        (isSubscriptionBlocked && !isCheckoutProcessing) || isOnboardingPage;
    const guardAllowedPages = getGuardAllowedPages(
        effectiveNeedsPlan,
        isCheckoutProcessing,
        canViewBilling,
    );

    const hasInitiallyLoaded = React.useRef(false);
    const [showLoader, setShowLoader] = React.useState(true);
    const [isFading, setIsFading] = React.useState(false);

    React.useEffect(() => {
        if (!isLoading) {
            hasInitiallyLoaded.current = true;
            setIsFading(true);
            const timer = setTimeout(() => {
                setShowLoader(false);
            }, 500);
            return () => clearTimeout(timer);
        } else if (!hasInitiallyLoaded.current) {
            setShowLoader(true);
            setIsFading(false);
        }
    }, [isLoading]);

    // Evita spinner infinito quando Supabase não responde (522 / Unhealthy)
    React.useEffect(() => {
        if (!isLoading || hasInitiallyLoaded.current) return;
        const watchdog = window.setTimeout(() => {
            hasInitiallyLoaded.current = true;
            setIsFading(true);
            setShowLoader(false);
        }, 45000);
        return () => clearTimeout(watchdog);
    }, [isLoading]);

    const isSuperadminArea = currentPage.startsWith('superadmin');
    if (isSuperadminArea) {
        return (
            <HeaderProvider>
                <SuperadminLayout>
                    <PageRenderer />
                </SuperadminLayout>
            </HeaderProvider>
        );
    }

    const isPartnerArea = isPartnerShellPage(currentPage);
    if (isPartnerArea) {
        return (
            <HeaderProvider>
                <PartnerLayout>
                    <PageRenderer />
                </PartnerLayout>
            </HeaderProvider>
        );
    }

    return (
        <HeaderProvider>
        <div className="relative flex h-dvh flex-col-reverse bg-background font-sans md:h-screen md:flex-row">
            {isCheckoutProcessing ? (
                <BootOverlay
                    title="Ativando seu plano"
                    subtitle="Liberando o sistema, aguarde..."
                />
            ) : showLoader ? (
                <BootOverlay
                    title="Adlitics"
                    subtitle="Carregando seu ambiente..."
                    fading={isFading}
                />
            ) : null}

            {!isOnboardingPage && (
                <Sidebar
                    isBlocked={shouldHideNavigation}
                    isOpen={isSidebarOpen}
                />
            )}

            <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', MOBILE_SHELL_TOP_SAFE_CLASS)}>
                {!shouldHideNavigation && (
                    <AuthenticatedHeader onToggleSidebar={() => setIsSidebarOpen((open) => !open)} />
                )}

                <SupportModeBanner />

                {orgError && (
                    <div className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
                        Não foi possível carregar dados do Supabase ({orgError}). O projeto pode estar sobrecarregado — tente novamente em alguns minutos.
                    </div>
                )}

                {!effectiveNeedsPlan && <SubscriptionAlerts />}
                <GlobalConnectionAlerts />

                <main className="relative box-border flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
                    <SubscriptionGuard allowedPages={guardAllowedPages}>
                        <div className="relative flex h-full min-h-0 flex-col">
                            <PageRenderer />
                        </div>
                    </SubscriptionGuard>
                </main>
            </div>
        </div>
        </HeaderProvider>
    );
};

export default AuthenticatedLayout;
